import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { Auth, DB } from '~/services/firebase';

import AuthTemplate from '~/layouts/Auth';
import { useToast } from '~/hooks/toast';
import getValidationErrors from '~/utils/getValidationErrors';

interface SignUpFormData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

const SignUp: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { addToast } = useToast();

  const [data, setData] = useState({} as SignUpFormData);
  const [errors, setErrors] = useState({} as any);
  const [passwordType, setPasswordType] = useState('password');
  const [password2Type, setPassword2Type] = useState('password');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required(
            t('index.field_required', { field: t('index.name') }),
          ),
          email: Yup.string()
            .required(t('index.field_required', { field: t('index.email') }))
            .email(t('index.field_invalid', { field: t('index.email') })),
          password: Yup.string()
            .required(t('index.field_required', { field: t('index.password') }))
            .min(
              8,
              t('index.field_min', { field: t('index.password'), number: 8 }),
            ),
          password_confirmation: Yup.string()
            .required(
              t('index.field_required', {
                field: t('index.password_confirmation'),
              }),
            )
            .min(
              8,
              t('index.field_min', {
                field: t('index.password_confirmation'),
                number: 8,
              }),
            ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { name, email, password } = data;

        const response = await createUserWithEmailAndPassword(
          Auth,
          email,
          password,
        );

        const {
          uid,
          metadata: { creationTime },
        } = response.user;

        await setDoc(doc(DB, 'users', uid), {
          uid,
          name,
          email,
          creationTime,
        });

        await setDoc(doc(DB, 'reports', uid), {
          company_id: '',
          data: {},
          user_id: uid,
        });

        addToast({
          type: 'success',
          description: t('index.signup_success'),
        });

        history.push('/');
      } catch (err: any) {
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
          return;
        }

        const { code } = err;

        if (code === 'auth/weak-password')
          setErrors({
            ...errors,
            password: 'Infome uma senha com no mínimo 6 caracteres.',
          });

        if (code === 'auth/email-already-in-use')
          setErrors({
            ...errors,
            email: 'O e-mail informado já está em uso.',
          });
      }
    },
    [data, t, errors, history, addToast],
  );

  return (
    <AuthTemplate>
      <div className="nk-block-head">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title">Fatia Prime Pizzaria</h5>
          <div className="nk-block-des">
            <p>Mais vendas, economia e eficiência em seu Delivery.</p>
          </div>
        </div>
      </div>

      {errors.message && (
        <div className="example-alert mb-3">
          <div className="alert alert-danger alert-icon alert-dismissible">
            <em className="icon ni ni-cross-circle" />
            {errors.message}
            <Link to="#close" className="close" data-dismiss="alert" />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <div className="form-label-group">
            <label htmlFor="input-name" className="form-label">
              {t('index.name')}
            </label>
          </div>
          <div className="form-control-wrap">
            <input
              type="text"
              id="input-name"
              className="form-control form-control-lg"
              placeholder={t('index.name_placeholder')}
              onChange={event => {
                setData({
                  ...data,
                  name: event.target.value,
                });
              }}
            />
            {errors.name && <span className="invalid">{errors.name}</span>}
          </div>
        </div>
        <div className="form-group">
          <div className="form-label-group">
            <label className="form-label" htmlFor="default-01">
              {t('index.email')}
            </label>
          </div>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder={t('index.email_placeholder')}
              onChange={event => {
                setData({
                  ...data,
                  email: event.target.value,
                });
              }}
            />
            {errors.email && <span className="invalid">{errors.email}</span>}
          </div>
        </div>
        <div className="form-group">
          <div className="form-label-group">
            <label className="form-label" htmlFor="password">
              {t('index.password')}
            </label>
          </div>
          <div className="form-control-wrap">
            <Link
              to="#eye"
              className="form-icon form-icon-right passcode-switch lg"
              data-target="password"
              onClick={() => {
                setPasswordType(
                  passwordType === 'password' ? 'text' : 'password',
                );
              }}
            >
              {passwordType === 'password' && (
                <em className="passcode-icon icon-show icon ni ni-eye" />
              )}
              {passwordType === 'text' && (
                <em className="passcode-icon icon-show icon ni ni-eye-off" />
              )}
            </Link>
            <input
              type={passwordType}
              className="form-control form-control-lg"
              placeholder={t('index.password_placeholder')}
              onChange={event => {
                setData({
                  ...data,
                  password: event.target.value,
                });
              }}
            />
            {errors.password && (
              <span className="invalid">{errors.password}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <div className="form-label-group">
            <label className="form-label" htmlFor="password">
              {t('index.password_confirmation')}
            </label>
          </div>
          <div className="form-control-wrap">
            <Link
              to="#eye"
              className="form-icon form-icon-right passcode-switch lg"
              data-target="password"
              onClick={() => {
                setPassword2Type(
                  password2Type === 'password' ? 'text' : 'password',
                );
              }}
            >
              {password2Type === 'password' && (
                <em className="passcode-icon icon-show icon ni ni-eye" />
              )}
              {password2Type === 'text' && (
                <em className="passcode-icon icon-show icon ni ni-eye-off" />
              )}
            </Link>
            <input
              type={password2Type}
              className="form-control form-control-lg"
              placeholder={t('index.password_confirmation_placeholder')}
              onChange={event => {
                setData({
                  ...data,
                  password_confirmation: event.target.value,
                });
              }}
            />
            {errors.password_confirmation && (
              <span className="invalid">{errors.password_confirmation}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <button
            type="submit"
            className="btn btn-lg btn-primary btn-block"
            disabled={loading}
          >
            {loading && (
              <div className="spinner-border spinner-border-sm" role="status" />
            )}
            {!loading && t('index.signup')}
          </button>
        </div>
      </form>
      <div className="form-note-s2 pt-4">
        {t('index.has_account')}
{' '}
<Link to="/sign-in">{t('index.signin')}</Link>
      </div>
    </AuthTemplate>
  );
};

export default SignUp;
